import { z } from 'zod';
import type { BookableResourceResponse, SaveBookableResourceRequest } from '@/shared/api';

/** Spiegelt `ResourceSchedule` im Server: lokales 30-Minuten-Raster innerhalb eines Tages. */
export const SLOT_MINUTES = 30;
export const MINUTES_PER_DAY = 24 * 60;
export const NAME_MAX_LENGTH = 80;
export const DESCRIPTION_MAX_LENGTH = 600;

/** „08:30“; das Tagesende heißt „24:00“, nicht „00:00“ des Folgetags. */
export function clockFromMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export function openingWindowLabel(
  resource: Pick<BookableResourceResponse, 'opensAtMinute' | 'closesAtMinute'>,
) {
  if (resource.opensAtMinute === 0 && resource.closesAtMinute === MINUTES_PER_DAY)
    return 'Ganztägig';
  return `${clockFromMinutes(resource.opensAtMinute)}–${clockFromMinutes(resource.closesAtMinute)} Uhr`;
}

export const OPENING_OPTIONS = Array.from(
  { length: MINUTES_PER_DAY / SLOT_MINUTES },
  (_, index) => index * SLOT_MINUTES,
);
export const CLOSING_OPTIONS = OPENING_OPTIONS.map((minutes) => minutes + SLOT_MINUTES);

export const resourceSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name fehlt')
      .max(NAME_MAX_LENGTH, `Höchstens ${NAME_MAX_LENGTH} Zeichen`),
    isActive: z.boolean(),
    opensAtMinute: z.number().int(),
    closesAtMinute: z.number().int(),
    hourlyPrice: z
      .number({ invalid_type_error: 'Preis fehlt' })
      .min(0, 'Der Preis darf nicht negativ sein.')
      .max(99_999_999.99, 'Der Preis ist zu hoch.'),
    descriptionDe: z
      .string()
      .max(DESCRIPTION_MAX_LENGTH, `Höchstens ${DESCRIPTION_MAX_LENGTH} Zeichen`),
    descriptionEn: z
      .string()
      .max(DESCRIPTION_MAX_LENGTH, `Höchstens ${DESCRIPTION_MAX_LENGTH} Zeichen`),
  })
  .refine((values) => values.opensAtMinute < values.closesAtMinute, {
    message: 'Das Ende muss nach dem Beginn liegen.',
    path: ['closesAtMinute'],
  });

export type ResourceValues = z.infer<typeof resourceSchema>;

export function resourceDefaults(resource?: BookableResourceResponse): ResourceValues {
  return resource
    ? {
        name: resource.name,
        isActive: resource.isActive,
        opensAtMinute: resource.opensAtMinute,
        closesAtMinute: resource.closesAtMinute,
        // Nach einem Währungswechsel des Objekts beginnt das Feld leer: der alte Betrag darf nicht
        // still in der neuen Währung gespeichert werden.
        hourlyPrice: resource.priceCurrencyMismatch ? Number.NaN : resource.hourlyPrice,
        descriptionDe: resource.descriptionDe ?? '',
        descriptionEn: resource.descriptionEn ?? '',
      }
    : {
        name: '',
        isActive: true,
        opensAtMinute: 8 * 60,
        closesAtMinute: 22 * 60,
        hourlyPrice: 0,
        descriptionDe: '',
        descriptionEn: '',
      };
}

export function resourceRequest(
  values: ResourceValues,
  currency: string,
): SaveBookableResourceRequest {
  return {
    ...values,
    name: values.name.trim(),
    priceCurrency: values.hourlyPrice > 0 ? currency : null,
    // Immer gesendet: ein leerer Text leert die Beschreibung (fehlend ließe sie stehen).
    descriptionDe: values.descriptionDe.trim(),
    descriptionEn: values.descriptionEn.trim(),
  };
}
